import { ref, watch, computed, type Ref } from 'vue';
import { defineStore, storeToRefs } from 'pinia';
import { useEventStore } from './eventStore';
import { EventDao, LicenceDao } from '@/dao';
import {
    Group,
    Race,
    Category,
    Station,
    StationRole,
    Eventinfo,
    EventTeam,
    type DbDataChangedNotificationDtoType,
    Licence,
    EventImplementation,
    DbDataChangedNotification,
    HeatPlan,
    type Gender,
} from '@/objects/generated';
import { BaseObjDao } from '@/dao/BaseObjDao';
import { type SelectLine, type SelectLineValueType } from '@/objects/SelectLine';
import { useCommonStore } from '@/stores';
import { LoaderWithReset } from '@/utilities/LoaderWithReset';
import { addToMapWithArray, arrayGroupbyToMap, arrayUnique } from '@/utilities/arrayUtils';
import type { BackupStationId, CategoryId, StationId } from '@/utilities/typealiases';
import WellKnown from '@/objects/generated/static/WellKnown';
import signalrSubscribe from '@/objects/generated/signalr/signalrSubscribe';

export const EventCommonValuesArray = [
    'Event',
    'Categories',
    'Groups',
    'Races',
    'Stations',
    'StationRoles',
    'EventTeams',
    'Licences',
    'EventImplementations',
    'HeatPlan',
    'EventTeam',
] as const;

export type EventCommonValues = (typeof EventCommonValuesArray)[number];
export type EventCommonStore = ReturnType<typeof useEventCommonStore>;
export type EventCommonStoreRef = ReturnType<typeof useEventCommonStoreRef>;

export const useEventCommonStore = defineStore('eventcommon', () => {
    const evStore = useEventStore();

    const { eventID, initpromise } = storeToRefs(evStore);

    const EventLoader = ref(
        new LoaderWithReset<Eventinfo | null>({
            defaultValue: null,
            loaderFunction: () => (eventID.value == 0 ? new Promise<null>((resolve) => resolve(null)) : EventDao.Event(eventID.value)),
            waitOnBeforeLoad: initpromise.value,
        }),
    );

    const CategoriesLoader = ref(
        new LoaderWithReset<Category[]>({
            defaultValue: [],
            loaderFunction: () => BaseObjDao.Load(Category, eventID.value),
            waitOnBeforeLoad: initpromise.value,
            postProcessFunction: (data) => data.sort((a, b) => (a.Sort ?? 0) - (b.Sort ?? 0) || a.Name.localeCompare(b.Name)),
        }),
    );

    const GroupsLoader = ref(
        new LoaderWithReset<Group[]>({
            defaultValue: [],
            loaderFunction: () => BaseObjDao.Load(Group, eventID.value),
            waitOnBeforeLoad: initpromise.value,
        }),
    );

    const RaceSorter = (a: Race, b: Race): number => a.Level - b.Level || a.Number - b.Number || a.Name!.localeCompare(b.Name!);
    const RacesLoader = ref(
        new LoaderWithReset<Race[]>({
            defaultValue: [],
            loaderFunction: () => BaseObjDao.Load(Race, eventID.value),
            waitOnBeforeLoad: initpromise.value,
            postProcessFunction: (data) => data.sort(RaceSorter),
        }),
    );

    const StationsLoader = ref(
        new LoaderWithReset<Station[]>({
            defaultValue: [],
            loaderFunction: () => BaseObjDao.Load(Station, eventID.value),
            waitOnBeforeLoad: initpromise.value,
            postProcessFunction: (data) => data.sort((a, b) => a.SortOrder - b.SortOrder || a.Name!.localeCompare(b.Name!)),
        }),
    );

    const StationRolesLoader = ref(
        new LoaderWithReset<StationRole[]>({
            defaultValue: [],
            loaderFunction: () => BaseObjDao.Load(StationRole, eventID.value),
            waitOnBeforeLoad: initpromise.value,
        }),
    );

    const EventTeamsLoader = ref(
        new LoaderWithReset<Map<number, EventTeam>>({
            defaultValue: new Map(),
            loaderFunction: async () => {
                const values = await BaseObjDao.Load(EventTeam, eventID.value);
                return new Map(values.map((v) => [v.ID, v]));
            },
            waitOnBeforeLoad: initpromise.value,
        }),
    );

    const LicencesLoader = ref(
        new LoaderWithReset<Licence[]>({
            defaultValue: [],
            loaderFunction: () => LicenceDao.GetLicences(Event.value?.FederationID ?? 0),
            waitOnBeforeLoad: initpromise.value,
        }),
    );

    const EventImplementationsLoader = ref(
        new LoaderWithReset<EventImplementation[]>({
            defaultValue: [],
            loaderFunction: () => BaseObjDao.Load(EventImplementation, eventID.value),
            waitOnBeforeLoad: initpromise.value,
        }),
    );

    const HeatPlanLoader = ref(
        new LoaderWithReset<HeatPlan[]>({
            defaultValue: [],
            loaderFunction: () => BaseObjDao.Load(HeatPlan, eventID.value),
            waitOnBeforeLoad: initpromise.value,
            postProcessFunction: (data) => data.sort((a, b) => a.SortOrder - b.SortOrder),
        }),
    );

    /**
     * We set up subscriptions for all the data we want to listen for changes on
     */
    const subscribeToDataChanged = () => {
        const subscriptionTypes: DbDataChangedNotificationDtoType[] = [
            'Eventinfo',
            'Race',
            'Category',
            'Group',
            'Station',
            'StationRole',
            'EventImplementation',
            'EventSetup',
            'HeatPlan',
            'EventTeam',
        ];

        for (const type of subscriptionTypes) {
            signalrSubscribe.DbDataChangedNotification({ EventID: eventID.value, DtoType: type }, dataChangedCallback);
        }
    };

    /**
     * Callback whenver we get a datachanged event from the backend.
     * We need to handle the different types of data that can be changed
     * @param eventId
     * @param DtoType
     * @param id
     */
    const dataChangedCallback = (notifications: DbDataChangedNotification[]) => {
        // TODO: For now we will ignore all the messages that is for one ID since we do not have an implementation for this yet.
        // And we always load all data for an event every time we get a message. If we dont ignore these we will get two messages for every change.
        // console.log('Data changed callback for', DtoType, 'for event', eventId, 'with id', id);

        const dtoTypes = Array.from(arrayGroupbyToMap(notifications, (n) => n.DtoType).keys());

        if (dtoTypes.includes('Race')) {
            RacesLoader.value.Reload();
        } else if (dtoTypes.includes('Category')) {
            CategoriesLoader.value.Reload();
        } else if (dtoTypes.includes('Group')) {
            GroupsLoader.value.Reload();
        } else if (dtoTypes.includes('Station')) {
            StationsLoader.value.Reload();
        } else if (dtoTypes.includes('StationRole')) {
            StationRolesLoader.value.Reload();
        } else if (dtoTypes.includes('EventImplementation')) {
            EventImplementationsLoader.value.Reload();
        } else if (dtoTypes.includes('EventSetup') || dtoTypes.includes('Eventinfo')) {
            EventLoader.value.Reload();
        } else if (dtoTypes.includes('HeatPlan')) {
            HeatPlanLoader.value.Reload();
        } else if (dtoTypes.includes('EventTeam')) {
            EventTeamsLoader.value.Reload();
        }
    };

    // make sure we have loaded ds
    const ensureLoaded = async (dataTypeToEnsureIsLoaded: EventCommonValues[]) => {
        // typed

        const promises = [];

        for (const ds of dataTypeToEnsureIsLoaded) {
            if (ds == 'Event') {
                promises.push(EventLoader.value.Load());
            }
            if (ds == 'Categories') {
                promises.push(CategoriesLoader.value.Load());
            }
            if (ds == 'Groups') {
                promises.push(GroupsLoader.value.Load());
            }
            if (ds == 'Races') {
                promises.push(RacesLoader.value.Load());
            }
            if (ds == 'Stations') {
                promises.push(StationsLoader.value.Load());
            }
            if (ds == 'StationRoles') {
                promises.push(StationRolesLoader.value.Load());
            }
            if (ds == 'EventTeams') {
                promises.push(EventTeamsLoader.value.Load());
            }
            if (ds == 'Licences') {
                promises.push(LicencesLoader.value.Load());
            }
            if (ds == 'EventImplementations') {
                promises.push(EventImplementationsLoader.value.Load());
            }
            if (ds == 'HeatPlan') {
                promises.push(HeatPlanLoader.value.Load());
            }
        }

        await Promise.all(promises);
    };

    const reset = async () => {
        EventLoader.value.Reset();
        CategoriesLoader.value.Reset();
        GroupsLoader.value.Reset();
        RacesLoader.value.Reset();
        StationsLoader.value.Reset();
        StationRolesLoader.value.Reset();
        EventTeamsLoader.value.Reset();
        LicencesLoader.value.Reset();
        EventImplementationsLoader.value.Reset();
        HeatPlanLoader.value.Reset();
    };

    // Helper promise to make sure we are initialized for the event
    watch(
        eventID,
        async (nv, ov) => {
            if (nv == ov || nv == 0) {
                return;
            }

            reset();

            console.log('Loading event common data for event', nv);
            await EventLoader.value.Load();
            await ensureLoaded(['Races', 'Stations', 'StationRoles', 'Categories', 'Groups']);
            // TODO: Since we are loading stuff from another store, maybe this should live somewhere else so we can have a set of "we need this data always"
            await useCommonStore().EnsureLoaded(['Federations']);

            subscribeToDataChanged();
        },
        { immediate: true },
    );

    const Event = computed(() => EventLoader.value.Value);

    // SelectLine
    const CategoriesAsSelectLine = computed<Map<SelectLineValueType, SelectLine>>(() => {
        const result = new Map<SelectLineValueType, SelectLine>();
        for (const x of CategoriesLoader.value.Value) {
            result.set(x.ID, { text: x.Name, value: x.ID });
        }
        return result;
    });

    const GroupsAsSelectLine = computed<Map<SelectLineValueType, SelectLine>>(() => {
        const result = new Map<SelectLineValueType, SelectLine>();
        for (const x of GroupsLoader.value.Value) {
            result.set(x.ID, { text: x.Name, value: x.ID });
        }
        return result;
    });

    const RacesAsSelectLine = computed<Map<SelectLineValueType, SelectLine>>(() => {
        const result = new Map<SelectLineValueType, SelectLine>();
        for (const x of RacesLoader.value.Value) {
            result.set(x.ID, { text: x.Name!, value: x.ID });
        }
        return result;
    });

    const StationsAsSelectLine = computed<Map<SelectLineValueType, SelectLine>>(() => {
        const result = new Map<SelectLineValueType, SelectLine>();
        for (const x of StationsLoader.value.Value) {
            result.set(x.ID, { text: x.Name!, value: x.ID });
        }
        return result;
    });

    const StationRolesAsSelectLine = computed<Map<SelectLineValueType, SelectLine>>(() => {
        const result = new Map<SelectLineValueType, SelectLine>();
        for (const x of StationRolesLoader.value.Value) {
            result.set(x.ID, { text: x.Name, value: x.ID });
        }
        return result;
    });

    /**
     * We create a quick map of all valid category combinations for the current event to make it easier to validate contestant category selection in the UI. The key is category
     */
    const availableCategoriesMap = computed(() => {
        const set = new Set<string>();

        // We need to add one line for each age in the range of the category, and for
        for (const category of CategoriesLoader.value.Value) {
            const ageFrom = category.AgeFrom ?? 0;
            const ageTo = category.AgeTo ?? 200;
            const gender = category.Gender ?? 'n';

            for (let age = ageFrom; age <= ageTo; age++) {
                set.add(`${category.ID}-${gender}-${age}`);
            }

            if (gender === 'n') {
                for (let age = ageFrom; age <= ageTo; age++) {
                    set.add(`${category.ID}-m-${age}`);
                    set.add(`${category.ID}-f-${age}`);
                }
            }
        }
        return set;
    });

    return {
        // typed
        Event: computed(() => Event.value),
        Categories: computed(() => CategoriesLoader.value.Value),
        Groups: computed(() => GroupsLoader.value.Value),
        Races: computed(() => RacesLoader.value.Value),
        Stations: computed(() => StationsLoader.value.Value),
        StationsMap: computed(() => {
            const map = new Map<number, Station>();
            for (const s of StationsLoader.value.Value) {
                map.set(s.ID, s);
            }
            return map;
        }),
        StationRoles: computed(() => StationRolesLoader.value.Value),
        /**
         * Map of all stationroles by their ID
         */
        StationRolesMap: computed(() => {
            const map = new Map<number, StationRole>();
            for (const r of StationRolesLoader.value.Value) {
                map.set(r.ID, r);
            }
            return map;
        }),
        EventTeams: computed(() => EventTeamsLoader.value.Value),
        EventImplementations: computed(() => EventImplementationsLoader.value.Value),
        HeatPlans: computed(() => HeatPlanLoader.value.Value),

        // SelectLine
        CategoriesAsSelectLine: CategoriesAsSelectLine,
        GroupsAsSelectLine: GroupsAsSelectLine,
        RacesAsSelectLine: RacesAsSelectLine,
        StationsAsSelectLine: StationsAsSelectLine,
        StationRolesAsSelectLine: StationRolesAsSelectLine,

        LicencesAsSelectLine: computed<Map<SelectLineValueType, SelectLine>>(() => {
            const result = new Map<SelectLineValueType, SelectLine>();
            result.set(null, { text: 'general.none', value: null });
            for (const x of LicencesLoader.value.Value) {
                result.set(x.ID, { text: x.Name, value: x.ID });
            }
            return result;
        }),
        EventTeamsAsSelectLine: computed<Map<SelectLineValueType, SelectLine>>(() => {
            const result = new Map<SelectLineValueType, SelectLine>();
            result.set(null, { text: 'general.none', value: null, separator: true });
            for (const [eventTeamID, eventTeam] of EventTeamsLoader.value.Value) {
                result.set(eventTeamID, { text: eventTeam.Name, value: eventTeam.ID });
            }
            return result;
        }),

        // SelectLine with null as Any
        CategoriesWithAllAsSelectLine: computed(() => {
            const map = new Map(CategoriesAsSelectLine.value);
            map.set(null, { text: 'general.all', value: null });
            return map;
        }),
        GroupsWithAllAsSelectLine: computed(() => {
            const map = new Map(GroupsAsSelectLine.value);
            map.set(null, { text: 'general.all', value: null });
            return map;
        }),
        RacesWithAllAsSelectLine: computed(() => {
            const map = new Map(RacesAsSelectLine.value);
            map.set(null, { text: 'general.all', value: null });
            return map;
        }),
        StationsWithAllAsSelectLine: computed(() => {
            const map = new Map(StationsAsSelectLine.value);
            map.set(null, { text: 'general.all', value: null });
            return map;
        }),
        StationRolesWithAllAsSelectLine: computed(() => {
            const map = new Map(StationRolesAsSelectLine.value);
            map.set(null, { text: 'general.all', value: null });
            return map;
        }),

        // SelectLine with null as None
        CategoriesWithNoneAsSelectLine: computed(() => {
            const map = new Map(CategoriesAsSelectLine.value);
            map.set(null, { text: 'general.none', value: null });
            return map;
        }),
        GroupsWithNoneAsSelectLine: computed(() => {
            const map = new Map(GroupsAsSelectLine.value);
            map.set(null, { text: 'general.none', value: null });
            return map;
        }),
        RacesWithNoneAsSelectLine: computed(() => {
            const map = new Map(RacesAsSelectLine.value);
            map.set(null, { text: 'general.none', value: null });
            return map;
        }),
        StationsWithNoneAsSelectLine: computed(() => {
            const map = new Map(StationsAsSelectLine.value);
            map.set(null, { text: 'general.none', value: null });
            return map;
        }),
        StationRolesWithNoneAsSelectLine: computed(() => {
            const map = new Map(StationRolesAsSelectLine.value);
            map.set(null, { text: 'general.none', value: null });
            return map;
        }),

        /**
         * Map of all backupstations to which station they are backup for
         * BackupStationId -> StationId
         */
        StationBackupStationIdToStationIdMap: computed(
            () =>
                new Map<BackupStationId, StationId>(
                    StationsLoader.value.Value.filter((s) => s.BackupFor !== null).map((s) => [
                        s.ID as BackupStationId,
                        s.BackupFor as StationId,
                    ]),
                ),
        ),

        /**
         * Map of all stationIds that have a backup station with which backupstations they have
         * StationId -> BackupStationId[]
         */
        StationIdToBackupStationIdsMap: computed(() => {
            const map = new Map<StationId, BackupStationId[]>();
            for (const s of StationsLoader.value.Value.filter((s) => s.BackupFor !== null)) {
                addToMapWithArray(map, s.BackupFor as StationId, s.ID as BackupStationId);
            }
            return map;
        }),

        CategoriesMap: computed(() => {
            const map = new Map<number, Category>();
            for (const c of CategoriesLoader.value.Value) {
                map.set(c.ID, c);
            }
            return map;
        }),

        CategoriesThatUsesShootingPenaltyTime: computed(
            () => new Set<CategoryId>(CategoriesLoader.value.Value.filter((r) => r.ShootingPenalty !== null).map((r) => r.ID)),
        ),

        GroupsMap: computed(() => {
            const map = new Map<number, Group>();
            for (const g of GroupsLoader.value.Value) {
                map.set(g.ID, g);
            }
            return map;
        }),

        /**
         * All the stationIds that are backup stations
         */
        BackupStationIds: computed(() => StationsLoader.value.Value.filter((s) => s.BackupFor != null).map((s) => s.ID)),

        /**
         * All the stationIds that are shooting control stations
         */
        ShootingControlStationIds: computed(
            () =>
                new Set<StationId>(
                    StationsLoader.value.Value.filter((s) => s.StationTypeID === WellKnown.STATIONTYPE_SHOOTING_CONTROL).map((s) => s.ID),
                ),
        ),

        /**
         * All the stationIds that are penalty lap stations
         */
        StationIdsThatArePenaltyLapStations: computed(
            () =>
                new Set<StationId>(
                    StationsLoader.value.Value.filter((s) => s.StationTypeID === WellKnown.STATIONTYPE_PENALTYLAP).map((s) => s.ID),
                ),
        ),

        /**
         * All the stationIds that has a stationrole that is set as startcontrol
         */
        StationsThatHasStartControlRole: computed(() =>
            arrayUnique(StationRolesLoader.value.Value.filter((sr) => sr.StartControl).map((sr) => sr.StationID)),
        ),

        /**
         * Map of all races by their ID
         */
        RacesMap: computed(() => {
            const map = new Map<number, Race>();
            for (const r of RacesLoader.value.Value) {
                map.set(r.ID, r);
            }
            return map;
        }),

        /**
         * Function to sort races
         */
        RaceSorter: RaceSorter,

        /**
         * OBS! You probably want to use this instead:
         * ```
         * useLoaders(AddLoader, ['STATIONROLES', 'STATIONS', 'RACES', 'GROUPS', 'CATEGORIES']);
         * ```
         */
        EnsureLoaded: ensureLoaded,

        /**
         * Validate if the categoryid, gender and age are a valid combination for the current event.
         */
        IsValidCategoryCombination: (categoryId: number, gender: Gender, age: number, relayTeamName?: string | null): boolean => {
            if ((relayTeamName ?? '').trim() !== '') {
                // We dont validate relay team categories since they can be dynamic and we dont want to have to update the frontend every time we add a new relay team category
                return true;
            }
            return availableCategoriesMap.value.has(`${categoryId}-${gender}-${age}`);
        },
    };
});

/**
 * A reactive reference to the common store.
 * @returns
 */
export const useEventCommonStoreRef = () => storeToRefs(useEventCommonStore());
